import React, { useMemo } from "react";
import { frameToPercent } from "../../../utils/timelineMath";

interface TimelineRulerProps {
  totalFrames: number;
  fps: number;
  animationDuration: number;
}

export const TimelineRuler = React.memo(function TimelineRuler({
  totalFrames,
  fps,
  animationDuration,
}: TimelineRulerProps) {
  const ticks = useMemo(() => {
    const majorStepSec = 1;
    const minorStepSec = 0.25;
    const count = Math.floor(animationDuration / minorStepSec);
    const next = [] as Array<{
      frame: number;
      isMajor: boolean;
      label: string | null;
    }>;

    for (let index = 0; index <= count; index += 1) {
      const seconds = index * minorStepSec;
      const frame = Math.round(seconds * fps);
      const isMajor =
        Math.abs(seconds / majorStepSec - Math.round(seconds / majorStepSec)) <
        1e-6;
      next.push({
        frame,
        isMajor,
        label: isMajor ? `${Math.round(seconds)}s` : null,
      });
    }

    if (next.length === 0 || next[next.length - 1].frame < totalFrames) {
      next.push({
        frame: totalFrames,
        isMajor: true,
        label: `${Math.round(animationDuration)}s`,
      });
    }

    return next;
  }, [animationDuration, fps, totalFrames]);

  return (
    <div
      className="timeline-ruler"
      style={{ position: "relative", height: 32 }}
      data-oid="s-bfi.g"
    >
      {ticks.map((tick) => {
        const left = frameToPercent(tick.frame, totalFrames) * 100;
        return (
          <div
            key={`${tick.frame}-${tick.isMajor ? "major" : "minor"}`}
            style={{
              position: "absolute",
              left: `${left}%`,
              top: 0,
              transform: "translateX(-0.5px)",
            }}
            data-oid="ri.zcke"
          >
            <div
              style={{
                width: 1,
                height: tick.isMajor ? 12 : 7,
                backgroundColor: tick.isMajor
                  ? "rgba(255,255,255,0.45)"
                  : "rgba(255,255,255,0.22)",
              }}
              data-oid="f35:8:."
            />

            {tick.label && (
              <div
                style={{
                  marginTop: 3,
                  fontSize: 10,
                  color: "rgba(255,255,255,0.68)",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                  letterSpacing: "0.04em",
                }}
                data-oid=".s62dfs"
              >
                {tick.label}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});
